import {
  DATA_CLASSES,
  buildLifecycle,
  normalizeDataClass,
} from './lifecycle';
import { buildPlaybackEligibility, derivePlaybackEligibility } from './playbackEligibility';

export const CATALOG_SCHEMA_VERSION = 1;

export const PLAYLIST_KINDS = Object.freeze({
  PROVIDER_MIRROR: 'provider_mirror',
  RHYTHME_NATIVE: 'rhythme_native',
  UNKNOWN: 'unknown',
});

export const DISPLAY_NAME_SOURCES = Object.freeze({
  PROVIDER: 'provider',
  USER: 'user',
  UNKNOWN: 'unknown',
});

export const AVAILABILITY_STATES = Object.freeze({
  AVAILABLE: 'available',
  PRIVATE: 'private',
  DELETED: 'deleted',
  REGION_BLOCKED: 'region_blocked',
  EMBED_BLOCKED: 'embed_blocked',
  AGE_RESTRICTED: 'age_restricted',
  REMOVED: 'removed',
  UNKNOWN: 'unknown',
});

export const SYNC_STATUSES = Object.freeze({
  IDLE: 'idle',
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETE: 'complete',
  PARTIAL: 'partial',
  FAILED: 'failed',
  UNAUTHORIZED: 'unauthorized',
  UNAVAILABLE: 'unavailable',
  UNKNOWN: 'unknown',
});

export const OCCURRENCE_ID_SOURCES = Object.freeze({
  PROVIDER_PLAYLIST_ITEM_ID: 'provider_playlist_item_id',
  PROVIDER_OCCURRENCE_ID: 'provider_occurrence_id',
  RHYTHME_RANDOM: 'rhythme_random',
  UNKNOWN: 'unknown',
});

export const OBSERVATION_ROLES = Object.freeze({
  AUTHORITATIVE: 'authoritative',
  RENDER_SNAPSHOT: 'render_snapshot',
  UNKNOWN: 'unknown',
});

export const RESOLUTION_STATUSES = Object.freeze({
  CONFIRMED: 'confirmed',
  PROBABLE: 'probable',
  AMBIGUOUS: 'ambiguous',
  WORK_ONLY: 'work_only',
  UNRESOLVED: 'unresolved',
  REJECTED: 'rejected',
  MIX: 'mix',
  UNKNOWN: 'unknown',
});

export const PROVIDER_INPUT_RESOLUTION_STATES = Object.freeze({
  RESOLVED: 'resolved',
  UNRESOLVED: 'provider_unresolved',
  UNKNOWN: 'unknown',
});

export const RECORDING_VERSION_TYPES = Object.freeze({
  STUDIO: 'studio',
  COVER: 'cover',
  REMIX: 'remix',
  EDIT: 'edit',
  LIVE: 'live',
  ACOUSTIC: 'acoustic',
  INSTRUMENTAL: 'instrumental',
  KARAOKE: 'karaoke',
  RADIO_EDIT: 'radio_edit',
  EXTENDED_MIX: 'extended_mix',
  RE_RECORDING: 're_recording',
  SLOWED: 'slowed',
  SPED_UP: 'sped_up',
  UNKNOWN: 'unknown',
});

export const ASSET_KINDS = Object.freeze({
  VIDEO: 'video',
  AUDIO: 'audio',
  TRACK: 'track',
  UNKNOWN: 'unknown',
});

const WRITABLE_PLAYLIST_KINDS = [PLAYLIST_KINDS.PROVIDER_MIRROR];
const WRITABLE_DISPLAY_NAME_SOURCES = [
  DISPLAY_NAME_SOURCES.PROVIDER,
  DISPLAY_NAME_SOURCES.USER,
];
const WRITABLE_SYNC_STATUSES = Object.values(SYNC_STATUSES).filter(
  (value) => value !== SYNC_STATUSES.UNKNOWN,
);
const WRITABLE_OCCURRENCE_SOURCES = Object.values(
  OCCURRENCE_ID_SOURCES,
).filter((value) => value !== OCCURRENCE_ID_SOURCES.UNKNOWN);

function requireObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value;
}

function requireNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a nonempty string`);
  }
  return value;
}

function optionalProviderText(value, name, fallback = null) {
  if (value === undefined || value === null) return fallback;
  if (typeof value !== 'string') {
    throw new TypeError(`${name} must be a string or null`);
  }
  return value;
}

function nonnegativeInteger(value, name, fallback = 0) {
  if (value === undefined) return fallback;
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${name} must be a nonnegative safe integer`);
  }
  return value;
}

function nullableDuration(value) {
  if (value === undefined || value === null) return null;
  return nonnegativeInteger(value, 'providerDurationMs');
}

function requireEnum(value, allowed, name, fallback) {
  const candidate = value === undefined ? fallback : value;
  if (!allowed.includes(candidate)) {
    throw new RangeError(`Unsupported ${name}: ${candidate}`);
  }
  return candidate;
}

function enumForRead(value, contract, fallback = 'unknown') {
  return Object.values(contract).includes(value) ? value : fallback;
}

function cloneStructuredArtists(value) {
  if (value === undefined || value === null) return null;
  if (!Array.isArray(value)) {
    throw new TypeError('providerStructuredArtists must be an array or null');
  }
  return value.map((entry) =>
    entry && typeof entry === 'object' && !Array.isArray(entry)
      ? { ...entry }
      : entry,
  );
}

function buildUserAuthorizedLifecycle(input) {
  const lifecycle = input ?? {};
  requireObject(lifecycle, 'lifecycle');
  if (
    lifecycle.dataClass !== undefined &&
    lifecycle.dataClass !== DATA_CLASSES.USER_AUTHORIZED_PROVIDER
  ) {
    throw new RangeError(
      'Phase 4A provider documents require user_authorized_provider lifecycle',
    );
  }
  return buildLifecycle({
    ...lifecycle,
    dataClass: DATA_CLASSES.USER_AUTHORIZED_PROVIDER,
    obtainedVia: lifecycle.obtainedVia ?? 'oauth_user_grant',
  });
}

function buildProviderSnapshot(input) {
  const snapshot = requireObject(input, 'providerSnapshot');
  return {
    title: optionalProviderText(snapshot.title, 'providerSnapshot.title', ''),
    description: optionalProviderText(
      snapshot.description,
      'providerSnapshot.description',
      null,
    ),
    thumbnailUrl: optionalProviderText(
      snapshot.thumbnailUrl,
      'providerSnapshot.thumbnailUrl',
      null,
    ),
    itemCount: nonnegativeInteger(
      snapshot.itemCount,
      'providerSnapshot.itemCount',
    ),
    privacyStatus: optionalProviderText(
      snapshot.privacyStatus,
      'providerSnapshot.privacyStatus',
      null,
    ),
    etag: optionalProviderText(snapshot.etag, 'providerSnapshot.etag', null),
    observedAt: snapshot.observedAt ?? null,
  };
}

function buildSync(input = {}, providerRevision = null) {
  const sync = requireObject(input, 'sync');
  return {
    status: requireEnum(
      sync.status,
      WRITABLE_SYNC_STATUSES,
      'sync status',
      SYNC_STATUSES.IDLE,
    ),
    syncRunId: optionalProviderText(
      sync.syncRunId,
      'sync.syncRunId',
      null,
    ),
    syncRunSeq: nonnegativeInteger(sync.syncRunSeq, 'sync.syncRunSeq'),
    startedAt: sync.startedAt ?? null,
    completedAt: sync.completedAt ?? null,
    lastSuccessfulSyncAt: sync.lastSuccessfulSyncAt ?? null,
    providerRevision:
      optionalProviderText(
        sync.providerRevision,
        'sync.providerRevision',
        providerRevision,
      ),
    cursor: optionalProviderText(sync.cursor, 'sync.cursor', null),
    failureCode: optionalProviderText(
      sync.failureCode,
      'sync.failureCode',
      null,
    ),
    sweepCompletedAt: sync.sweepCompletedAt ?? null,
  };
}

function buildLease(input) {
  if (input === undefined || input === null) return null;
  const lease = requireObject(input, 'lease');
  return {
    ownerClientId: requireNonEmptyString(
      lease.ownerClientId,
      'lease.ownerClientId',
    ),
    acquiredAt: lease.acquiredAt ?? null,
    leaseExpiresAt: lease.leaseExpiresAt ?? null,
  };
}

function buildObservation(input, role, { includeOwner, includeCategory, includeAvailability }) {
  const observation = requireObject(input, 'observation');
  const result = {
    providerTitle: optionalProviderText(
      observation.providerTitle,
      'observation.providerTitle',
      null,
    ),
    providerChannelId: optionalProviderText(
      observation.providerChannelId,
      'observation.providerChannelId',
      null,
    ),
    providerChannelTitle: optionalProviderText(
      observation.providerChannelTitle,
      'observation.providerChannelTitle',
      null,
    ),
  };

  if (includeOwner) {
    result.providerOwnerChannelTitle = optionalProviderText(
      observation.providerOwnerChannelTitle,
      'observation.providerOwnerChannelTitle',
      null,
    );
  }

  result.providerStructuredArtists = cloneStructuredArtists(
    observation.providerStructuredArtists,
  );
  result.providerDurationMs = nullableDuration(
    observation.providerDurationMs,
  );
  result.providerThumbnailUrl = optionalProviderText(
    observation.providerThumbnailUrl,
    'observation.providerThumbnailUrl',
    null,
  );
  result.providerPublishedAt = observation.providerPublishedAt ?? null;

  if (includeCategory) {
    result.providerCategoryId = optionalProviderText(
      observation.providerCategoryId,
      'observation.providerCategoryId',
      null,
    );
  }

  if (includeAvailability) {
    result.availability = requireEnum(
      observation.availability,
      Object.values(AVAILABILITY_STATES),
      'availability',
      AVAILABILITY_STATES.UNKNOWN,
    );
  }

  result.observedAt = observation.observedAt ?? null;
  result.observationRole = role;
  return result;
}

export function buildUserPlaylist(input) {
  const value = requireObject(input, 'UserPlaylist input');
  const kind = requireEnum(
    value.kind,
    WRITABLE_PLAYLIST_KINDS,
    'playlist kind',
    PLAYLIST_KINDS.PROVIDER_MIRROR,
  );
  const providerSnapshot = buildProviderSnapshot(value.providerSnapshot);
  const knownItemCount = nonnegativeInteger(
    value.knownItemCount,
    'knownItemCount',
    providerSnapshot.itemCount,
  );

  return {
    catalogSchemaVersion: CATALOG_SCHEMA_VERSION,
    userPlaylistId: requireNonEmptyString(
      value.userPlaylistId,
      'userPlaylistId',
    ),
    kind,
    provider: requireNonEmptyString(value.provider, 'provider'),
    providerPlaylistId: requireNonEmptyString(
      value.providerPlaylistId,
      'providerPlaylistId',
    ),
    providerOwnerChannelId: optionalProviderText(
      value.providerOwnerChannelId,
      'providerOwnerChannelId',
      null,
    ),
    providerSnapshot,
    displayName: optionalProviderText(
      value.displayName,
      'displayName',
      providerSnapshot.title,
    ),
    displayNameSource: requireEnum(
      value.displayNameSource,
      WRITABLE_DISPLAY_NAME_SOURCES,
      'displayNameSource',
      DISPLAY_NAME_SOURCES.PROVIDER,
    ),
    knownItemCount,
    loadedItemCount: nonnegativeInteger(
      value.loadedItemCount,
      'loadedItemCount',
    ),
    playableItemCount: nonnegativeInteger(
      value.playableItemCount,
      'playableItemCount',
    ),
    availability: requireEnum(
      value.availability,
      Object.values(AVAILABILITY_STATES),
      'availability',
      AVAILABILITY_STATES.UNKNOWN,
    ),
    sync: buildSync(value.sync, providerSnapshot.etag),
    lease: buildLease(value.lease),
    lifecycle: buildUserAuthorizedLifecycle(value.lifecycle),
    importedAt: value.importedAt ?? null,
    createdAt: value.createdAt ?? null,
    updatedAt: value.updatedAt ?? null,
  };
}

export function buildUserPlaylistItem(input) {
  const value = requireObject(input, 'UserPlaylistItem input');
  const occurrenceIdSource = requireEnum(
    value.occurrenceIdSource,
    WRITABLE_OCCURRENCE_SOURCES,
    'occurrenceIdSource',
  );
  const providerPlaylistItemId = optionalProviderText(
    value.providerPlaylistItemId,
    'providerPlaylistItemId',
    null,
  );
  if (
    occurrenceIdSource ===
      OCCURRENCE_ID_SOURCES.PROVIDER_PLAYLIST_ITEM_ID &&
    !providerPlaylistItemId
  ) {
    throw new TypeError(
      'providerPlaylistItemId is required for provider_playlist_item_id',
    );
  }

  return {
    catalogSchemaVersion: CATALOG_SCHEMA_VERSION,
    userPlaylistItemId: requireNonEmptyString(
      value.userPlaylistItemId,
      'userPlaylistItemId',
    ),
    occurrenceIdSource,
    provider: requireNonEmptyString(value.provider, 'provider'),
    providerPlaylistItemId,
    providerAssetKey: requireNonEmptyString(
      value.providerAssetKey,
      'providerAssetKey',
    ),
    providerAssetId: requireNonEmptyString(
      value.providerAssetId,
      'providerAssetId',
    ),
    position: nonnegativeInteger(value.position, 'position'),
    observation: buildObservation(
      value.observation,
      OBSERVATION_ROLES.RENDER_SNAPSHOT,
      {
        includeOwner: true,
        includeCategory: false,
        includeAvailability: false,
      },
    ),
    availability: requireEnum(
      value.availability,
      Object.values(AVAILABILITY_STATES),
      'availability',
      AVAILABILITY_STATES.UNKNOWN,
    ),
    playbackEligibility: buildPlaybackEligibility(
      value.playbackEligibility === undefined
        ? {
          availability: value.availability,
          embeddable: value.embeddable,
        }
        : {
          ...value.playbackEligibility,
          availability: value.availability,
        },
    ),
    lastKnownGoodObservation: value.lastKnownGoodObservation == null
      ? null
      : buildObservation(
        value.lastKnownGoodObservation,
        OBSERVATION_ROLES.RENDER_SNAPSHOT,
        {
          includeOwner: true,
          includeCategory: false,
          includeAvailability: false,
        },
      ),
    addedToPlaylistAt: value.addedToPlaylistAt ?? null,
    resolution: {
      status: RESOLUTION_STATUSES.UNRESOLVED,
      recordingId: null,
      workId: null,
      confidence: null,
      mappingVersion: null,
      resolvedAt: null,
    },
    lastSeenSyncRunSeq: nonnegativeInteger(
      value.lastSeenSyncRunSeq,
      'lastSeenSyncRunSeq',
    ),
    removedFromPlaylistAt: value.removedFromPlaylistAt ?? null,
    lifecycle: buildUserAuthorizedLifecycle(value.lifecycle),
    createdAt: value.createdAt ?? null,
    updatedAt: value.updatedAt ?? null,
  };
}

export function buildUserPrivateProviderAsset(input) {
  const value = requireObject(input, 'UserPrivateProviderAsset input');
  return {
    catalogSchemaVersion: CATALOG_SCHEMA_VERSION,
    providerAssetKey: requireNonEmptyString(
      value.providerAssetKey,
      'providerAssetKey',
    ),
    provider: requireNonEmptyString(value.provider, 'provider'),
    providerAssetId: requireNonEmptyString(
      value.providerAssetId,
      'providerAssetId',
    ),
    assetKind: requireEnum(
      value.assetKind,
      Object.values(ASSET_KINDS),
      'assetKind',
      ASSET_KINDS.UNKNOWN,
    ),
    observation: buildObservation(
      value.observation,
      OBSERVATION_ROLES.AUTHORITATIVE,
      {
        includeOwner: false,
        includeCategory: true,
        includeAvailability: true,
      },
    ),
    playbackEligibility: buildPlaybackEligibility(
      value.playbackEligibility === undefined
        ? {
          availability: value.observation?.availability,
          embeddable: value.embeddable,
        }
        : {
          ...value.playbackEligibility,
          availability: value.observation?.availability,
        },
    ),
    lastKnownGoodObservation: value.lastKnownGoodObservation == null
      ? null
      : buildObservation(
        value.lastKnownGoodObservation,
        OBSERVATION_ROLES.AUTHORITATIVE,
        {
          includeOwner: false,
          includeCategory: true,
          includeAvailability: true,
        },
      ),
    firstObservedAt: value.firstObservedAt ?? null,
    lastObservedAt: value.lastObservedAt ?? null,
    observedVia: Array.isArray(value.observedVia)
      ? value.observedVia.map((entry) =>
          requireNonEmptyString(entry, 'observedVia entry'),
        )
      : [],
    promotion: {
      promotedAt: null,
      promotedRunId: null,
    },
    lifecycle: buildUserAuthorizedLifecycle(value.lifecycle),
    createdAt: value.createdAt ?? null,
    updatedAt: value.updatedAt ?? null,
  };
}

export function buildPlaylistSyncRun(input) {
  const value = requireObject(input, 'PlaylistSyncRun input');
  return {
    catalogSchemaVersion: CATALOG_SCHEMA_VERSION,
    runSeq: nonnegativeInteger(value.runSeq, 'runSeq'),
    startedAt: value.startedAt ?? null,
    completedAt: value.completedAt ?? null,
    status: requireEnum(
      value.status,
      WRITABLE_SYNC_STATUSES,
      'sync status',
      SYNC_STATUSES.RUNNING,
    ),
    pagesFetched: nonnegativeInteger(value.pagesFetched, 'pagesFetched'),
    itemsSeen: nonnegativeInteger(value.itemsSeen, 'itemsSeen'),
    itemsCreated: nonnegativeInteger(value.itemsCreated, 'itemsCreated'),
    itemsUpdated: nonnegativeInteger(value.itemsUpdated, 'itemsUpdated'),
    itemsRemoved: nonnegativeInteger(value.itemsRemoved, 'itemsRemoved'),
    failureCode: optionalProviderText(
      value.failureCode,
      'failureCode',
      null,
    ),
    clientId: requireNonEmptyString(value.clientId, 'clientId'),
    lifecycle: buildUserAuthorizedLifecycle(value.lifecycle),
  };
}

export function normalizeUserPlaylistForRead(raw) {
  const value = requireObject(raw, 'UserPlaylist document');
  return {
    ...value,
    kind: enumForRead(value.kind, PLAYLIST_KINDS),
    displayNameSource: enumForRead(
      value.displayNameSource,
      DISPLAY_NAME_SOURCES,
    ),
    availability: enumForRead(
      value.availability,
      AVAILABILITY_STATES,
    ),
    sync: value.sync
      ? {
          ...value.sync,
          status: enumForRead(value.sync.status, SYNC_STATUSES),
        }
      : value.sync,
    lifecycle: value.lifecycle
      ? {
          ...value.lifecycle,
          dataClass: normalizeDataClass(value.lifecycle.dataClass),
        }
      : value.lifecycle,
  };
}

export function normalizeUserPlaylistItemForRead(raw) {
  const value = requireObject(raw, 'UserPlaylistItem document');
  return {
    ...value,
    occurrenceIdSource: enumForRead(
      value.occurrenceIdSource,
      OCCURRENCE_ID_SOURCES,
    ),
    availability: enumForRead(
      value.availability,
      AVAILABILITY_STATES,
    ),
    playbackEligibility: derivePlaybackEligibility({
      availability: value.availability,
      embeddable: value.playbackEligibility?.embeddable,
      checkedAt: value.playbackEligibility?.checkedAt ?? null,
      reason: value.playbackEligibility?.reason ?? null,
    }),
    lastKnownGoodObservation: value.lastKnownGoodObservation ?? null,
    resolution: value.resolution
      ? {
          ...value.resolution,
          status: enumForRead(
            value.resolution.status,
            RESOLUTION_STATUSES,
          ),
        }
      : value.resolution,
    lifecycle: value.lifecycle
      ? {
          ...value.lifecycle,
          dataClass: normalizeDataClass(value.lifecycle.dataClass),
        }
      : value.lifecycle,
  };
}

export function normalizeRecordingVersionType(value) {
  return enumForRead(
    value,
    RECORDING_VERSION_TYPES,
    RECORDING_VERSION_TYPES.UNKNOWN,
  );
}
